import { LanguageCode } from '../contexts/LanguageContext';

export interface Translations {
  nav: {
    home: string;
    about: string;
    skills: string;
    languages: string;
    projects: string;
    visitors: string;
    contact: string;
  };
  hero: {
    name: string;
    tagline: string;
    cta: string;
  };
  about: {
    title: string;
    paragraph1: string;
    paragraph2: string;
  };
  skills: {
    title: string;
    frontend: string;
    backend: string;
    tools: string;
    softSkills: string;
  };
  languagesSection: {
    title: string;
    native: string;
    professional: string;
    intermediate: string;
    turkish: string;
    english: string;
  };
  projects: {
    title: string;
    viewProject: string;
    viewCode: string;
    aiProjects: string;
    fullstackProjects: string;
  };
  contact: {
    title: string;
    description: string;
    email: string;
    phone: string;
    location: string;
    github: string;
    linkedin: string;
  };
  visitorsMap?: {
    title: string;
    subtitle: string;
    totalVisitors: string;
    uniqueLocations: string;
    totalVisits: string;
    countries: string;
    visitors: string;
  };
}

export const translations: Record<LanguageCode, Translations> = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      skills: 'Skills',
      languages: 'Languages',
      projects: 'Projects',
      visitors: 'Visitors',
      contact: 'Contact',
    },
    hero: {
      name: 'Batuhan Bulbul',
      tagline: 'AI Engineer & Full-Stack Developer — Building intelligent products with LLMs, automation, and scalable architectures.',
      cta: 'View My Work',
    },
    about: {
      title: 'About Me',
      paragraph1: "I'm an AI Engineer with 8+ years of experience in software development. I started my career in end-user testing, which sharpened my empathy for real user needs, then transitioned into backend engineering with PHP/Laravel. Over the years, I've built web-based game applications and played an active role in the backend architecture of LMS platforms — covering data modeling, API design, SCORM integrations, and performance optimization. In my personal projects, I also work as a full-stack developer, handling everything from frontend to backend and deployment.",
      paragraph2: "Today, I prototype and ship AI-driven features such as automated reporting & analytics, speech & voice evaluation, and workflow automation — bringing measurable productivity gains to products. I embrace Vibe Coding and AI-assisted development to accelerate and optimize my workflow. I actively explore Generative AI, LLM-based agents, and RAG architectures to build next-generation intelligent products.",
    },
    skills: {
      title: 'Skills',
      frontend: 'Frontend',
      backend: 'Backend',
      tools: 'Tools',
      softSkills: 'Soft Skills',
    },
    languagesSection: {
      title: 'Languages',
      native: 'Native',
      professional: 'Professional',
      intermediate: 'Intermediate',
      turkish: 'Turkish',
      english: 'English',
    },
    projects: {
      title: 'Projects',
      viewProject: 'View Project',
      viewCode: 'View Code',
      aiProjects: 'AI Projects',
      fullstackProjects: 'Full-Stack Projects',
    },
    contact: {
      title: 'Get In Touch',
      description: "I'm open to discussing new opportunities, AI-powered product ideas, or collaborations. Let's build something intelligent together.",
      email: 'Email',
      phone: 'Phone',
      location: 'Location',
      github: 'GitHub',
      linkedin: 'LinkedIn',
    },
  },
  tr: {
    nav: {
      home: 'Ana Sayfa',
      about: 'Hakkımda',
      skills: 'Yetenekler',
      languages: 'Diller',
      projects: 'Projeler',
      visitors: 'Ziyaretçiler',
      contact: 'İletişim',
    },
    hero: {
      name: 'Batuhan Bülbül',
      tagline: 'AI Engineer & Full-Stack Developer — LLM, otomasyon ve ölçeklenebilir mimarilerle akıllı ürünler geliştiriyorum.',
      cta: 'Çalışmalarımı Gör',
    },
    about: {
      title: 'Hakkımda',
      paragraph1: "8+ yıllık yazılım geliştirme deneyimine sahip bir AI Engineer'ım. Kariyerime son kullanıcı testleriyle başladım, bu süreçte gerçek kullanıcı ihtiyaçlarına yönelik güçlü bir bakış açısı kazandım. Ardından PHP/Laravel ile backend mühendisliğine yöneldim. Yıllar içerisinde web tabanlı oyun uygulamaları geliştirdim ve LMS platformlarının backend mimarisinde aktif rol aldım — veri modelleme, API tasarımı, SCORM entegrasyonları ve performans optimizasyonu konularında deneyim kazandım. Kendi kişisel projelerimde ise frontend'den backend'e ve deployment'a kadar tüm süreci yöneterek full-stack geliştirme yapıyorum.",
      paragraph2: "Bugün, otomatik raporlama ve analitik, konuşma ve ses değerlendirme, iş akışı otomasyonu gibi yapay zeka destekli özellikler prototipliyor ve üretim ortamına taşıyorum. Vibe Coding yaklaşımını ve AI destekli geliştirme araçlarını benimseyerek kendi geliştirme süreçlerimi hızlandırıyor ve optimize ediyorum. Generative AI, LLM tabanlı agentlar ve RAG mimarileri üzerine aktif olarak çalışıyorum.",
    },
    skills: {
      title: 'Yetenekler',
      frontend: 'Frontend',
      backend: 'Backend',
      tools: 'Araçlar',
      softSkills: 'Kişisel Yetenekler',
    },
    languagesSection: {
      title: 'Diller',
      native: 'Ana Dil',
      professional: 'Profesyonel',
      intermediate: 'Orta Seviye',
      turkish: 'Türkçe',
      english: 'İngilizce',
    },
    projects: {
      title: 'Projeler',
      viewProject: 'Projeyi Gör',
      viewCode: 'Kodu Gör',
      aiProjects: 'AI Projeleri',
      fullstackProjects: 'Full-Stack Projeler',
    },
    contact: {
      title: 'İletişim',
      description: "Yeni fırsatlar, yapay zeka destekli ürün fikirleri veya iş birliklerini konuşmaya her zaman açığım. Birlikte akıllı bir şey inşa edelim.",
      email: 'E-posta',
      phone: 'Telefon',
      location: 'Konum',
      github: 'GitHub',
      linkedin: 'LinkedIn',
    },
  },
};

export const getTranslations = (language: LanguageCode): Translations => {
  return translations[language];
};
